import { apiClient } from './apiClient';
import {
  CreateReviewRequest,
  ReviewResponse,
  ReviewCapability,
  AdReview,
  ReviewHistoryEntry,
} from '@agency-smm/shared';

export class ReviewTimeoutError extends Error {
  constructor() {
    super('Review analysis timed out. Please try again.');
    this.name = 'ReviewTimeoutError';
  }
}

export class ReviewLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReviewLimitError';
  }
}

export const reviewApi = {
  async createReview(
    workspaceId: string,
    postId: string,
    request: CreateReviewRequest
  ): Promise<ReviewResponse> {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new ReviewTimeoutError()), 15000);
    });
    try {
      const result = await Promise.race([
        apiClient.post<ReviewResponse>(
          `/api/workspaces/${workspaceId}/posts/${postId}/review`,
          request
        ),
        timeoutPromise,
      ]);
      return result;
    } catch (error: unknown) {
      if (error instanceof ReviewTimeoutError) {
        throw error;
      }
      const msg = error instanceof Error ? error.message : 'Review failed';
      if (msg.includes('limit') || msg.includes('subscription')) {
        throw new ReviewLimitError(msg);
      }
      throw error;
    }
  },

  async getReview(
    workspaceId: string,
    postId: string
  ): Promise<{ review: AdReview | null; reviewsRemaining?: number }> {
    return apiClient.get(`/api/workspaces/${workspaceId}/posts/${postId}/review`);
  },

  async getReviewHistory(workspaceId: string, postId: string): Promise<ReviewHistoryEntry[]> {
    return apiClient.get(`/api/workspaces/${workspaceId}/posts/${postId}/reviews`);
  },

  async getReviewCapability(): Promise<ReviewCapability> {
    return apiClient.get<ReviewCapability>('/api/billing/review-capability');
  },
};
